import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { LuLink as LinkIcon, LuCode } from 'react-icons/lu';
import Loading from '@/components/Loading';
import { Tag } from '@/components/Tag';
import useFetchAdminImportRequestItemShow from '../../api/useFetchAdminImportRequestItemShow';
import useUpdateAdminImportRequestItem from '../../api/useUpdateAdminImportRequestItem';
import useDoneAdminImportRequestItem from '../../api/useDoneAdminImportRequestItem';
import useCloseAdminImportRequestItem from '../../api/useCloseAdminImportRequestItem';
import { AdminButton } from '../../components/AdminButton';
import AdminBackLink from '../../components/atoms/AdminBackLink';
import AdminErrorMessage from '../../components/atoms/AdminErrorMessage';
import AdminHtmlParseArea from '../../components/molecules/AdminHtmlParseArea';
import AdminContentEntryList from '../../components/molecules/AdminContentEntryList';
import {
  mergeContentEntries,
  type ContentEntry,
} from '../../components/molecules/AdminContentEntryList';
import { STATUS_LABELS, STATUS_TAG_COLORS } from '../../constants/status';

type JobContentItem = { key?: string; label?: string; content?: string };

function parseJobContentJson(value: unknown): Array<JobContentItem> {
  if (Array.isArray(value)) return value as Array<JobContentItem>;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value) as unknown;
      return Array.isArray(parsed) ? (parsed as Array<JobContentItem>) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function toContentEntries(items: JobContentItem[]): ContentEntry[] {
  return items.map((item) => ({
    label: item.key ?? item.label ?? '',
    content: item.content ?? '',
  }));
}

type ItemEditPageProps = {
  importRequestId: number;
  itemId: number;
};

export default function ItemEditPage({ importRequestId, itemId }: ItemEditPageProps) {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [jobContent, setJobContent] = useState<ContentEntry[]>([]);
  const [agentContent, setAgentContent] = useState<ContentEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [htmlModalOpen, setHtmlModalOpen] = useState(false);

  const { data: showItem, isLoading } = useFetchAdminImportRequestItemShow(importRequestId, itemId);
  const updateMutation = useUpdateAdminImportRequestItem();
  const doneMutation = useDoneAdminImportRequestItem();
  const closeMutation = useCloseAdminImportRequestItem();

  // サーバーデータでフォームを初期化
  useEffect(() => {
    if (showItem && !isInitialized) {
      setTitle(showItem.outer_job_post_title ?? '');
      setCompanyName(showItem.outer_job_post_company_name ?? '');
      setJobContent(
        toContentEntries(parseJobContentJson(showItem.outer_job_post_job_content_json))
      );
      setAgentContent(
        toContentEntries(parseJobContentJson(showItem.outer_job_post_agent_content_json))
      );
      setIsInitialized(true);
    }
  }, [showItem, isInitialized]);

  const navigateBack = () => {
    navigate({
      to: '/admin',
      search: { tab: 'import_requests', importRequestId },
    });
  };

  // 変更があるかどうか判定
  const hasChanges = useMemo(() => {
    if (!showItem) return false;
    const serverTitle = showItem.outer_job_post_title ?? '';
    const serverCompany = showItem.outer_job_post_company_name ?? '';
    const serverJob = toContentEntries(
      parseJobContentJson(showItem.outer_job_post_job_content_json)
    );
    const serverAgent = toContentEntries(
      parseJobContentJson(showItem.outer_job_post_agent_content_json)
    );
    return (
      title !== serverTitle ||
      companyName !== serverCompany ||
      JSON.stringify(jobContent) !== JSON.stringify(serverJob) ||
      JSON.stringify(agentContent) !== JSON.stringify(serverAgent)
    );
  }, [showItem, title, companyName, jobContent, agentContent]);

  const handleParsed = (result: {
    job_content: ContentEntry[];
    agent_content: ContentEntry[];
    title: string | null;
    company_name: string | null;
  }) => {
    setJobContent((prev) => mergeContentEntries(prev, result.job_content));
    setAgentContent((prev) => mergeContentEntries(prev, result.agent_content));
    if (result.title) setTitle(result.title);
    if (result.company_name) setCompanyName(result.company_name);
    setHtmlModalOpen(false);
  };

  // 保存して対応済み
  const handleSubmitAll = async () => {
    if (!hasChanges) return;
    setError(null);
    const outerJobPost = {
      title: title.trim() || undefined,
      company_name: companyName.trim() || undefined,
      job_content: jobContent.map(({ label, content }) => ({
        key: label.trim() || undefined,
        label: label.trim() || undefined,
        content: content.trim() || undefined,
      })),
      agent_content: agentContent.map(({ label, content }) => ({
        key: label.trim() || undefined,
        label: label.trim() || undefined,
        content: content.trim() || undefined,
      })),
    };
    try {
      await updateMutation.mutateAsync({
        importRequestId,
        itemId,
        outer_job_post: outerJobPost,
      });
      await doneMutation.mutateAsync({ importRequestId, itemId });
      navigateBack();
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存に失敗しました');
    }
  };

  // 更新なしで対応済み
  const handleDoneOnly = () => {
    setError(null);
    doneMutation.mutate(
      { importRequestId, itemId },
      {
        onSuccess: () => navigateBack(),
        onError: (err) =>
          setError(err instanceof Error ? err.message : '対応済みの記録に失敗しました'),
      }
    );
  };

  // 求人クローズ
  const handleClose = () => {
    if (!window.confirm('この求人をクローズしますか？')) return;
    setError(null);
    closeMutation.mutate(
      { importRequestId, itemId },
      {
        onSuccess: () => navigateBack(),
        onError: (err) =>
          setError(err instanceof Error ? err.message : '求人クローズの記録に失敗しました'),
      }
    );
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[360px] items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (!showItem) {
    return (
      <div className="flex flex-col gap-4">
        <AdminBackLink to="/admin" search={{ tab: 'import_requests', importRequestId }} />
        <p className="text-sub">項目が見つかりませんでした。</p>
      </div>
    );
  }

  const isDone = showItem.status === 'done';

  return (
    <div className="flex flex-col gap-4">
      <AdminBackLink to="/admin" search={{ tab: 'import_requests', importRequestId }} />
      <div className="rounded border border-solid border-border bg-white p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-bold break-words text-main">
              {showItem.outer_job_post_title ?? '—'}
            </h2>
            <Tag color={STATUS_TAG_COLORS[showItem.status] ?? 'gray'} size="sm">
              {STATUS_LABELS[showItem.status] ?? showItem.status}
            </Tag>
            {showItem.outer_job_post_url != null && showItem.outer_job_post_url !== '' && (
              <a
                href={showItem.outer_job_post_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex shrink-0 items-center gap-1.5 text-sm whitespace-nowrap text-[#4188ff] underline hover:opacity-80"
              >
                <LinkIcon className="size-4 shrink-0" />
              </a>
            )}
          </div>
          {!isDone && (
            <button
              type="button"
              onClick={() => setHtmlModalOpen(true)}
              className="inline-flex items-center gap-1.5 rounded border border-solid border-border bg-white px-3 py-1.5 text-sm text-main hover:bg-gray-50"
            >
              <LuCode className="size-4 text-sub" />
              HTMLから取り込み
            </button>
          )}
        </div>

        <div className="flex flex-col gap-3 text-sm">
          <div>
            <label htmlFor="edit-title" className="mb-1 block font-medium text-main">
              タイトル
            </label>
            <input
              id="edit-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              readOnly={isDone}
              className="box-border w-full rounded border border-solid border-border px-3 py-2 text-main read-only:bg-gray-50"
            />
          </div>
          <div>
            <label htmlFor="edit-company" className="mb-1 block font-medium text-main">
              企業名
            </label>
            <input
              id="edit-company"
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              readOnly={isDone}
              className="box-border w-full rounded border border-solid border-border px-3 py-2 text-main read-only:bg-gray-50"
            />
          </div>
          <AdminContentEntryList
            title="求人情報"
            entries={jobContent}
            onChange={setJobContent}
            keyPrefix="job"
          />
          <AdminContentEntryList
            title="エージェント向け項目"
            entries={agentContent}
            onChange={setAgentContent}
            keyPrefix="agent"
          />
        </div>

        <AdminErrorMessage message={error} />

        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={navigateBack}
              className="rounded border border-solid border-border bg-white px-3 py-1.5 text-sm text-main hover:bg-gray-50"
            >
              戻る
            </button>
            <AdminButton
              label={closeMutation.isPending ? '送信中...' : '求人クローズ'}
              variant="danger"
              size="sm"
              onClick={handleClose}
              disabled={isDone || closeMutation.isPending || doneMutation.isPending}
            />
          </div>
          {!isDone && (
            <div className="flex items-center gap-2">
              {hasChanges && (
                <AdminButton
                  label={
                    updateMutation.isPending
                      ? '保存中...'
                      : doneMutation.isPending
                        ? '送信中...'
                        : '保存して対応済み'
                  }
                  size="sm"
                  onClick={handleSubmitAll}
                  disabled={updateMutation.isPending || doneMutation.isPending}
                />
              )}
              <AdminButton
                label={doneMutation.isPending ? '送信中...' : '更新なしで対応済み'}
                size="sm"
                onClick={handleDoneOnly}
                disabled={doneMutation.isPending}
              />
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={htmlModalOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setHtmlModalOpen(false);
        }}
      >
        <DialogContent className="flex w-full max-w-2xl flex-col rounded-lg p-6 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]!">
          <DialogTitle className="sr-only">HTMLから取り込み</DialogTitle>
          <h3 className="mb-4 text-base font-bold text-main">HTMLから取り込み</h3>
          <AdminHtmlParseArea importRequestId={importRequestId} onParsed={handleParsed} />
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={() => setHtmlModalOpen(false)}
              className="rounded border border-solid border-border bg-white px-3 py-1.5 text-sm text-main hover:bg-gray-50"
            >
              閉じる
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
